import React from 'react';
import {
  getDocumentSourcePath,
  getExternalFolder,
  getSourceLabel,
  resolveDocumentPortalPath
} from '../utils/docsPaths';
import {
  filterProductNavItems,
  isPlatformInternalDocPath,
  isPlatformNavDocument,
  isProductFacingDocument
} from '../utils/platformInternalDocs';

// API Documentation Structure
const apiModules = [
  {
    name: 'overview',
    title: '📋 Overview',
    items: [
      { title: 'OpenAPI Reference', route: '/api-reference' },
      { title: 'Project Context', path: '/docs/core/PROJECT_CONTEXT.md' },
      { title: 'Status & Roadmap', path: '/docs/core/PROMANG_STATUS_AND_ROADMAP.md' },
      { title: 'Versjonshistorikk', path: '/docs/core/VERSION_HISTORY.md' },
      { title: 'V2026-2 (pågående)', path: '/docs/core/versions/V2026-2.md' },
      { title: 'V2026-1', path: '/docs/core/versions/V2026-1.md' }
    ]
  },
  {
    name: 'project',
    title: '📁 Project',
    items: [
      { title: 'Overview', path: '/docs/modules/PROJECT.md' },
      { title: 'Document archive', path: '/docs/modules/DOCUMENT_ARCHIVE.md' },
      { title: 'API Reference', path: '/docs/modules/PROJECT_API.md' },
      { title: 'Project activities & Tripletex hours', path: '/docs/modules/PROJECT_ACTIVITIES.md' },
      { title: 'Item lists (merge to order, manual invoiced)', path: '/docs/modules/ITEM_LISTS.md' },
      { title: 'Project time registration & approval (UI)', path: '/docs/modules/PROJECT_TIME_REGISTRATION.md' },
      { title: 'Work orders (list, delete)', path: '/docs/modules/WORK_ORDERS.md' },
      { title: 'Workflow & Processes', path: '/docs/modules/PROJECT_WORKFLOW.md' },
      { title: 'Recent Changes', path: '/docs/modules/PROJECT_CHANGES.md' }
    ]
  },
  {
    name: 'report',
    title: '📋 Report',
    items: [
      { title: 'Overview', path: '/docs/modules/REPORT.md' },
      { title: 'API Reference', path: '/docs/modules/REPORT_API.md' },
      { title: 'Workflow & Processes', path: '/docs/modules/REPORT_WORKFLOW.md' },
      { title: 'Service report agreement (UI)', path: '/docs/modules/service/REPORT_AGREEMENT_UI.md' },
      { title: 'Report admin menu (UI)', path: '/docs/modules/service/REPORT_ADMIN_MENU.md' },
      { title: 'Report edit drawer (UI)', path: '/docs/modules/service/REPORT_EDIT_DRAWER.md' },
      { title: 'Report view redesign (UI)', path: '/docs/modules/service/REPORT_VIEW_REDESIGN.md' },
      { title: 'Report dev menu (UI)', path: '/docs/modules/service/REPORT_DEV_MENU.md' },
      { title: 'Recent Changes', path: '/docs/modules/REPORT_CHANGES.md' }
    ]
  },
  {
    name: 'order',
    title: '🛒 Order',
    items: [
      { title: 'Overview', path: '/docs/modules/ORDER.md' },
      { title: 'API Reference', path: '/docs/modules/ORDER_API.md' },
      { title: 'Recent Changes', path: '/docs/modules/ORDER_CHANGES.md' }
    ]
  },
  {
    name: 'customer',
    title: '👥 Customer',
    items: [
      { title: 'Overview', path: '/docs/modules/CUSTOMER.md' },
      { title: 'API Reference', path: '/docs/modules/CUSTOMER_API.md' },
      { title: 'Recent Changes', path: '/docs/modules/CUSTOMER_CHANGES.md' },
      { title: 'E-postlogg', path: '/docs/modules/OUTBOUND_COMMUNICATIONS.md' }
    ]
  },
  {
    name: 'user',
    title: '👤 User',
    items: [
      { title: 'Overview', path: '/docs/modules/USER.md' },
      { title: 'API Reference', path: '/docs/modules/USER_API.md' },
      { title: 'Next of Kin API', path: '/docs/modules/user_next_of_kin_api.md' },
      { title: 'Edit my profile (rediger min profil)', path: '/docs/modules/USER_PROFILE_EDITING.md' },
      { title: 'User guide: my profile (onboarding)', path: '/docs/modules/USER_GUIDE_PROFILE.md' },
      { title: 'Recent Changes', path: '/docs/modules/USER_CHANGES.md' }
    ]
  },
  {
    name: 'service-agreement',
    title: '📝 Service Agreement',
    items: [
      { title: 'Overview', path: '/docs/modules/SERVICE_AGREEMENT.md' },
      { title: 'API Reference', path: '/docs/modules/SERVICE_AGREEMENT_API.md' },
      { title: 'Recent Changes', path: '/docs/modules/SERVICE_AGREEMENT_CHANGES.md' }
    ]
  },
  {
    name: 'hse-ks',
    title: '🛡️ HSE/KS',
    items: [
      { title: 'Avvik og arbeidsordre', path: '/docs/modules/quality/DEVIATIONS_WORK_ORDERS.md' },
      { title: 'HSE/KS Modules - API', path: '/docs/modules/HSE_KS_MODULES_API.md' },
      { title: 'ISO Compliance Roadmap', path: '/docs/modules/ISO_COMPLIANCE_ROADMAP.md' },
      { title: 'Status & Implementation', path: '/docs/core/PROMANG_STATUS_AND_ROADMAP.md' },
      { title: 'Menu Structure', path: '/docs/modules/HSE_KS_MENU_STRUCTURE.md' },
      { title: 'Routes Implementation', path: '/docs/modules/HSE_KS_ROUTES_IMPLEMENTATION.md' }
    ],
    submenus: {
      'Context & Climate': [
        { title: 'Context Issues', path: '/docs/modules/HSE_KS_MODULES_API.md#context--climate-change-management-iso-90012024-amendment' },
        { title: 'Interested Parties', path: '/docs/modules/HSE_KS_MODULES_API.md#context--climate-change-management-iso-90012024-amendment' }
      ],
      'Documents': [
        { title: 'HSE overview', path: '/docs/modules/HSE.md' },
        { title: 'HSE/KS Documents API', path: '/docs/modules/HSE_KS_DOCUMENTS_API.md' },
        { title: 'Recent changes', path: '/docs/modules/HSE_CHANGES.md' },
        { title: 'Controlled Documents', path: '/docs/modules/HSE_KS_MODULES_API.md#controlled-documents-iso-9001-75' },
        { title: 'Document Structure Implementation', path: '/docs/core/DOCUMENT_STRUCTURE_IMPLEMENTATION.md' }
      ],
      'Events': [
        { title: 'Management Events', path: '/docs/modules/HSE_KS_MODULES_API.md#management-events-iso-9001-102--iso-45001' },
        { title: 'CAPA Actions', path: '/docs/modules/HSE_KS_MODULES_API.md#corrective-action-capa' }
      ],
      'Risk & Safety': [
        { title: 'Risk Register', path: '/docs/modules/HSE_KS_MODULES_API.md#risk-management-iso-9001-61--iso-45001' },
        { title: 'Job Safety Analysis', path: '/docs/modules/HSE_KS_MODULES_API.md#risk-management-iso-9001-61--iso-45001' }
      ],
      'Competency': [
        { title: 'Competency Matrix', path: '/docs/modules/HSE_KS_MODULES_API.md#competency-management-iso-9001-72' }
      ],
      'Evaluation': [
        { title: 'Internal Audits', path: '/docs/modules/HSE_KS_MODULES_API.md#audit-management-iso-9001-92' },
        { title: 'Management Review', path: '/docs/modules/HSE_KS_MODULES_API.md#management-review-iso-9001-93' },
        { title: 'KPI System', path: '/docs/modules/HSE_KS_MODULES_API.md#kpi-management-iso-9001-91' }
      ]
    }
  },
  {
    name: 'inventory',
    title: '📦 Inventory',
    items: [
      { title: 'Overview & Stocktaking', path: '/docs/modules/INVENTORY_STOCKTAKING_SYSTEM.md' },
      { title: 'Picking list (plukkliste)', path: '/docs/modules/INVENTORY_PICKING_LIST.md' },
      { title: 'Purchase System (Innkjøp)', path: '/docs/modules/PURCHASE_SYSTEM.md' },
      { title: 'API Reference', path: '/docs/modules/INVENTORY_API.md' },
      { title: 'Product model year', path: '/docs/external/api/modules/PRODUCT_MODEL_YEAR.md' },
      { title: 'Product replaced-by (successor)', path: '/docs/external/api/modules/PRODUCT_REPLACED_BY_PRODUCT.md' },
      { title: 'Product categories API', path: '/docs/external/api/modules/PRODUCT_CATEGORIES_API.md' },
      { title: 'Recent Changes', path: '/docs/modules/INVENTORY_CHANGES.md' }
    ]
  },
  {
    name: 'event',
    title: '📅 Event',
    items: [
      { title: 'Overview', path: '/docs/modules/EVENT.md' },
      { title: 'Microsoft calendar sync', path: '/docs/modules/EVENT_MICROSOFT_CALENDAR.md' },
      { title: 'Status Updates', path: '/docs/modules/EVENT_STATUS_UPDATES.md' },
      { title: 'Missing Events', path: '/docs/modules/reports_missing_events.md' },
      { title: 'Recent Changes', path: '/docs/modules/EVENT_CHANGES.md' }
    ]
  },
  {
    name: 'oppdatering',
    title: '✍️ Oppdatering',
    items: [
      { title: 'Overview (StatusUpdate)', path: '/docs/modules/STATUS_UPDATES.md' },
      { title: 'Agents', path: '/docs/core/OPPDATERING_AGENTS.md' }
    ]
  },
  {
    name: 'task',
    title: '✅ Task',
    items: [
      { title: 'Overview', path: '/docs/modules/TASK.md' },
      { title: 'Project tasks & templates', path: '/docs/modules/PROJECT_TASKS.md' },
      { title: 'Todos workspace & journal', path: '/docs/modules/TODOS_WORKSPACE.md' },
      { title: 'API Reference', path: '/docs/external/api/modules/TASK_API.md' },
      { title: 'Recent Changes', path: '/docs/external/api/modules/TASK_CHANGES.md' }
    ]
  },
  {
    name: 'time-registration',
    title: '⏰ Time Registration',
    items: [
      { title: 'Overview', path: '/docs/modules/TIME_REGISTRATION.md' },
      { title: 'Timeliste-eksport', path: '/docs/modules/HOURS_TIMESHEET_EXPORT.md' },
      { title: 'Rask / hurtig registrering', path: '/docs/modules/HOURS_QUICK_ENTRY.md' },
      { title: 'Del opp timer', path: '/docs/modules/HOURS_SPLIT.md' },
      { title: 'Avtalt overtid', path: '/docs/modules/AGREED_OVERTIME.md' },
      { title: 'Lønnsgrunnlag', path: '/docs/modules/PAYROLL_BASIS.md' }
    ]
  },
  {
    name: 'absence',
    title: '🏥 Absence',
    items: [
      { title: 'Overview', path: '/docs/modules/ABSENCE.md' }
    ]
  },
  {
    name: 'cases',
    title: '📧 Cases',
    items: [
      { title: 'Overview', path: '/docs/modules/cases/README.md' },
      { title: 'List View', path: '/docs/modules/cases/CASES_LIST_VIEW.md' },
      { title: 'Timeline feed', path: '/docs/modules/cases/CASE_TIMELINE_FEED.md' },
      { title: 'API changes', path: '/docs/modules/cases/CASE_CHANGES.md' },
      { title: 'Mentions & email', path: '/docs/modules/cases/CASE_ITEM_MENTIONS_AND_EMAIL.md' }
    ]
  },
  {
    name: 'tools',
    title: '🔧 Tools',
    items: [{ title: 'Overview', path: '/docs/modules/TOOLS.md' }]
  },
  {
    name: 'journals',
    title: '📓 Journals',
    items: [{ title: 'Overview', path: '/docs/modules/JOURNALS.md' }]
  },
  {
    name: 'drawings',
    title: '📐 Drawings / Tegninger',
    items: [
      { title: 'Overview', path: '/docs/modules/DRAWINGS.md' },
      { title: 'User guide (houses, floors, upload)', path: '/docs/modules/USER_GUIDE_DRAWINGS.md' },
      { title: 'Kinds + Andre + replace confirm', path: '/docs/modules/DRAWINGS.md#house-and-floor-structure' },
      { title: 'PDF upload (one page)', path: '/docs/modules/DRAWINGS.md#one-page-per-drawing-pdf-upload' },
      { title: 'Avvik og arbeidsordre', path: '/docs/modules/quality/DEVIATIONS_WORK_ORDERS.md' },
    ]
  },
  {
    name: 'text-documents',
    title: '📄 Text documents (letters)',
    items: [{ title: 'Overview', path: '/docs/modules/TEXT_DOCUMENTS.md' }]
  },
  {
    name: 'meeting-minutes',
    title: '📝 Meeting minutes',
    items: [
      { title: 'API', path: '/docs/external/api/modules/MEETING_MINUTES.md' },
      { title: 'Frontend', path: '/docs/external/frontend/modules/MEETING_MINUTES.md' },
    ]
  },
  {
    name: 'vehicle-service',
    title: '🚗 Vehicle Service',
    items: [
      { title: 'Overview', path: '/docs/modules/VEHICLE_SERVICE.md' },
      { title: 'API Reference', path: '/docs/modules/VEHICLE_SERVICE_API.md' },
      { title: 'Recent Changes', path: '/docs/modules/VEHICLE_SERVICE_CHANGES.md' }
    ]
  },
  {
    name: 'drawings',
    title: '📐 Drawings / Tegninger',
    items: [
      { title: 'Overview', path: '/docs/modules/DRAWINGS.md' },
      { title: 'User guide (houses, floors, upload)', path: '/docs/modules/USER_GUIDE_DRAWINGS.md' },
      { title: 'Kinds + Andre + replace confirm', path: '/docs/modules/DRAWINGS.md#house-and-floor-structure' },
      { title: 'PDF upload (one page)', path: '/docs/modules/DRAWINGS.md#one-page-per-drawing-pdf-upload' }
    ]
  }
];

// Frontend Documentation Structure
const frontendModules = [
  {
    name: 'getting-started',
    title: '🚀 Getting Started',
    items: [
      { title: 'Frontend Overview', path: '/docs/core/FRONTEND_OVERVIEW.md' },
      { title: 'Dashboard navigation', path: '/docs/external/frontend/core/DASHBOARD_NAVIGATION.md' },
      { title: 'Versjonshistorikk', path: '/docs/core/VERSION_HISTORY.md' },
      { title: 'V2026-2 (pågående)', path: '/docs/core/versions/V2026-2.md' },
      { title: 'V2026-1', path: '/docs/core/versions/V2026-1.md' }
    ]
  },
  {
    name: 'components',
    title: '🧩 Components',
    items: [
      { title: 'Brand & form design system', path: '/docs/core/PROMANG_BRAND_DESIGN.md' },
      { title: 'Loading & motion system', path: '/docs/core/PROMANG_LOADING_SYSTEM.md' },
      { title: 'Tabs & overflow menu (PMTabs)', path: '/docs/core/PM_TABS.md' },
      { title: 'Document Manager V6', path: '/docs/core/DOCUMENT_MANAGER_V6.md' },
      { title: 'Dashboard Header', path: '/docs/core/DASHBOARD_HEADER.md' },
      { title: 'QR Code Scanner', path: '/docs/core/QR_CODE_SCANNER.md' }
    ]
  },
  {
    name: 'modules',
    title: '📦 Modules',
    items: [
      { title: 'Time Registration', path: '/docs/modules/legacy/TIME_REGISTRATION.md' },
      { title: 'Absence', path: '/docs/modules/legacy/ABSENCE.md' },
      { title: 'Drawings / Tegninger', path: '/docs/modules/DRAWINGS.md' },
      { title: 'Drawings user guide', path: '/docs/modules/USER_GUIDE_DRAWINGS.md' },
      { title: 'Avvik og arbeidsordre', path: '/docs/modules/quality/DEVIATIONS_WORK_ORDERS.md' },
      { title: 'HSE/KS Modules', path: '/docs/modules/HSE_KS_MODULES.md' },
      { title: 'Item Handling System', path: '/docs/modules/ITEM_HANDLING_SYSTEM.md' },
      { title: 'Item Consumption List', path: '/docs/modules/ITEM_CONSUMPTION_LIST.md' }
    ]
  },
  {
    name: 'reports',
    title: '📊 Reports',
    items: [
      { title: 'Report View V2', path: '/docs/modules/REPORT_VIEW_V2.md' },
      { title: 'Report List Filters', path: '/docs/modules/REPORT_LIST_FILTERS.md' }
    ]
  },
  {
    name: 'statistics',
    title: '📈 Statistics & Tags',
    items: [
      { title: 'Statistics and Tags', path: '/docs/modules/STATISTICS_AND_TAGS.md' }
    ]
  },
  {
    name: 'ai-ceo',
    title: '🤖 AI & CEO Agent',
    items: [
      { title: 'Microsoft: test mail & AI-innbokser', path: '/docs/integrations/MICROSOFT_MAIL_TEST_AND_AI_INBOXES.md' },
      { title: 'Microsoft: oppsett, AI-jobber og kalender', path: '/docs/integrations/MICROSOFT_AI_AND_CALENDAR_SETUP.md' },
      { title: 'Microsoft OAuth callback (UI)', path: '/docs/integrations/MICROSOFT_OAUTH_CALLBACK_UI.md' },
      { title: 'Microsoft: utgående e-post (Graph)', path: '/docs/integrations/microsoft-outbound-mail.md' },
      { title: 'AI e-post svar-utkast', path: '/docs/integrations/AI_REPLY_DRAFTS.md' },
      { title: 'Automile (kjørebok)', path: '/docs/integrations/AUTOMILE.md' }
    ]
  },
  {
    name: 'billing',
    title: '💰 Billing & Accounting',
    items: [
      { title: 'Stripe order payments', path: '/docs/integrations/STRIPE.md' },
      { title: 'Accounting Summary', path: '/docs/modules/ACCOUNTING_SUMMARY.md' }
    ]
  },
  {
    name: 'development',
    title: '💻 Development',
    items: [
      { title: 'Deployment', path: '/docs/core/DEPLOYMENT.md' },
      { title: 'Promang Mobile (Expo)', path: '/docs/integrations/PROMANG_MOBILE_EXPO.md' }
    ]
  }
];

// Core Systems (API only)
const apiCoreSystems = [
  { title: 'Versjonshistorikk', path: '/docs/core/VERSION_HISTORY.md' },
  { title: 'Document System', path: '/docs/core/DOCUMENT_SYSTEM.md' },
  { title: 'AI usage events (metering)', path: '/docs/core/AI_USAGE_EVENTS.md' },
  { title: 'Admin Dashboard API', path: '/docs/external/api/core/ADMIN_DASHBOARD.md' },
  { title: 'Ops Monitor', path: '/docs/external/api/core/OPS_MONITOR.md' },
  { title: 'Heroku Scheduler', path: '/docs/external/api/core/HEROKU_SCHEDULER.md' },
  { title: 'Platform broadcasts', path: '/docs/external/api/core/PLATFORM_BROADCASTS.md' },
  { title: 'ModernProductSelectorV2', path: '/docs/core/MODERN_PRODUCT_SELECTOR_V2.md' },
  { title: 'Promang Table View', path: '/docs/core/TABLE_VIEW.md' },
  { title: 'Tabs & overflow menu (PMTabs)', path: '/docs/core/PM_TABS.md' },
  { title: 'Promang brand & form design', path: '/docs/core/PROMANG_BRAND_DESIGN.md' },
  { title: 'Promang loading & motion system', path: '/docs/core/PROMANG_LOADING_SYSTEM.md' },
  { title: 'Promang rich text editor', path: '/docs/core/PROMANG_RICH_TEXT_EDITOR.md' },
  { title: 'PDF Generation', path: '/docs/core/PDF_GENERATION.md' },
  { title: 'Background Jobs', path: '/docs/core/BACKGROUND_JOBS.md' },
  { title: 'Database Safety', path: '/docs/core/DATABASE_SAFETY.md' },
  { title: 'Accounting Summary', path: '/docs/modules/ACCOUNTING_SUMMARY.md' }
];

// Integrations (API only)
const apiIntegrations = [
  { title: 'Tripletex employee sync', path: '/docs/integrations/TRIPLETEX_EMPLOYEE_SYNC.md' },
  { title: 'Microsoft: utgående e-post (Graph)', path: '/docs/integrations/microsoft-outbound-mail.md' },
  { title: 'Microsoft OAuth (API)', path: '/docs/integrations/MICROSOFT_OAUTH_API.md' },
  { title: 'Microsoft: test mail & AI-innbokser', path: '/docs/integrations/MICROSOFT_MAIL_TEST_AND_AI_INBOXES.md' },
  { title: 'Microsoft: oppsett, AI-jobber og kalender', path: '/docs/integrations/MICROSOFT_AI_AND_CALENDAR_SETUP.md' },
  { title: 'Delte AI-innbokser (regler & PATCH)', path: '/docs/integrations/COMPANY_AI_MAILBOXES.md' },
  { title: 'SVV Integration', path: '/docs/integrations/SVV_INTEGRATION.md' },
  { title: 'Activity Sync', path: '/docs/integrations/ACTIVITY_SYNC.md' },
  { title: 'Phonero Integration', path: '/docs/integrations/phonero_communication_integration.md' },
  { title: 'Manual Phonero Calls', path: '/docs/integrations/manual_phonero_calls.md' },
  { title: 'AI e-post svar-utkast (stil + kontekst)', path: '/docs/integrations/AI_REPLY_DRAFTS.md' },
  { title: 'Automile (API + UI)', path: '/docs/integrations/AUTOMILE.md' },
  { title: 'Stripe order payments', path: '/docs/integrations/STRIPE.md' },
  { title: 'Promang Mobile (Expo)', path: '/docs/integrations/PROMANG_MOBILE_EXPO.md' },
  { title: 'Promang IFC Viewer (3D/BIM)', path: '/docs/integrations/IFC_VIEWER.md' }
];

/** Fallback when documentation-index.json is unavailable. */
const staticPlatformInternalItems = [
  { title: 'Dokumentasjonsarkitektur (regel)', path: '/docs/core/DOCUMENTATION_ARCHITECTURE_RULE.md' },
  { title: 'Oppryddingsinventar (fase 1)', path: '/docs/core/DOCUMENTATION_CLEANUP_INVENTORY.md' },
  { title: 'Dokumentasjonsinventar (fase 1 peeker)', path: '/docs/core/DOCUMENTATION_INVENTORY_PHASE1.md' },
  { title: 'Dokumentasjonsinventar (fase 2)', path: '/docs/core/documentation-migration-inventory.md' },
  { title: 'Migreringsplan for dokumentasjon', path: '/docs/core/documentation-migration-plan.md' },
  { title: 'Kjøreplan for dokumentasjonsarkitektur', path: '/docs/core/DOCUMENTATION_EXECUTION_PLAN.md' },
  { title: 'Kjøreplan fase 2', path: '/docs/core/DOCUMENTATION_EXECUTION_PLAN_PHASE2.md' },
  { title: 'Komponent-preview teknisk forslag', path: '/docs/core/component-preview-technical-proposal.md' },
  { title: 'Preview-runtime pilot', path: '/docs/core/PREVIEW_RUNTIME_PILOT.md' },
  { title: 'Dokumentasjonsvalidering', path: '/docs/core/DOCUMENTATION_VALIDATION.md' }
];

const generatedFolderLabels = {
  ai: 'AI (metadata)',
  core: 'Core (metadata)',
  integrations: 'Integrasjoner (metadata)',
  modules: 'Moduler (metadata)'
};

const externalFolderLabels = {
  ai: 'AI',
  components: 'Komponenter',
  core: 'Core',
  integrations: 'Integrasjoner',
  modules: 'Moduler'
};

function mergeSidebarItems(staticItems, generatedItems) {
  const byPath = new Map();
  [...staticItems, ...generatedItems].forEach((item) => {
    if (!item.path || byPath.has(item.path)) return;
    byPath.set(item.path, item);
  });
  return [...byPath.values()].sort((a, b) => a.title.localeCompare(b.title));
}

function getGeneratedFolder(sourcePath) {
  const match = sourcePath?.match(/^public\/docs\/([^/]+)\//);
  return match?.[1] ?? 'core';
}

function mapGeneratedSidebarItem(document, externalManifest) {
  return {
    title: document.title,
    description: document.description,
    path: resolveDocumentPortalPath(document, externalManifest),
    source: document.source,
    sourceLabel: getSourceLabel(document.source),
    type: document.type,
    status: document.status,
    visibility: document.visibility
  };
}

function buildDocsOwnedGeneratedSections(documents) {
  const grouped = new Map();
  (documents ?? [])
    .filter((document) => document.source === 'docs')
    .filter(isProductFacingDocument)
    .map((document) => ({
      ...mapGeneratedSidebarItem(document),
      folder: getGeneratedFolder(document.sourcePath)
    }))
    .filter((item) => item.path && !isPlatformInternalDocPath(item.path))
    .forEach((item) => {
      const key = generatedFolderLabels[item.folder] ? item.folder : 'core';
      const items = grouped.get(key) ?? [];
      items.push(item);
      grouped.set(key, items);
    });

  return ['core', 'modules', 'integrations', 'ai']
    .filter((key) => grouped.has(key))
    .map((key) => ({
      name: `generated-${key}`,
      title: generatedFolderLabels[key],
      items: grouped.get(key).sort((a, b) => a.title.localeCompare(b.title))
    }));
}

function buildExternalGeneratedSections(documents, docsType, externalManifest) {
  if (!externalManifest) return [];

  const grouped = new Map();
  (documents ?? [])
    .filter((document) => document.source === docsType)
    .filter(isProductFacingDocument)
    .map((document) => ({
      ...mapGeneratedSidebarItem(document, externalManifest),
      folder: getExternalFolder(getDocumentSourcePath(document))
    }))
    .filter((item) => item.path && !isPlatformInternalDocPath(item.path))
    .forEach((item) => {
      const key = externalFolderLabels[item.folder] ? item.folder : 'core';
      const items = grouped.get(key) ?? [];
      items.push(item);
      grouped.set(key, items);
    });

  return ['core', 'modules', 'integrations', 'components', 'ai']
    .filter((key) => grouped.has(key))
    .map((key) => ({
      name: `external-${docsType}-${key}`,
      title: `${getSourceLabel(docsType)}: ${externalFolderLabels[key]}`,
      items: grouped.get(key).sort((a, b) => a.title.localeCompare(b.title))
    }));
}

function buildGeneratedSections(documents, docsType, externalManifest) {
  return [
    ...buildDocsOwnedGeneratedSections(documents),
    ...buildExternalGeneratedSections(documents, docsType, externalManifest)
  ];
}

function matchesItemSearch(item, searchQuery) {
  const query = searchQuery.trim().toLowerCase();
  if (!query) return true;
  return [item.title, item.path, item.route, item.description, item.source, item.sourceLabel, item.type, item.status]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
}

// Export function to get modules structure for export functions
export const getModulesStructure = (docsType) => {
  const modules = docsType === 'api' ? apiModules : frontendModules;
  
  // Add core systems and integrations for API
  const allModules = [...modules];
  if (docsType === 'api') {
    allModules.push({
      name: 'core-systems',
      title: 'Core Systems',
      items: filterProductNavItems(apiCoreSystems)
    });
    allModules.push({
      name: 'integrations',
      title: 'Integrations',
      items: apiIntegrations
    });
  }
  
  return allModules;
};

const DocsSidebar = ({ docsType, isOpen, onClose, onFileSelect, searchQuery }) => {
  const [generatedPlatformItems, setGeneratedPlatformItems] = React.useState([]);
  const [generatedSections, setGeneratedSections] = React.useState([]);

  React.useEffect(() => {
    let mounted = true;

    async function loadGeneratedSidebarItems() {
      try {
        const indexResponse = await fetch('/generated/documentation-index.json');
        if (!indexResponse.ok) return;
        const payload = await indexResponse.json();
        let externalManifest = null;
        try {
          const externalManifestResponse = await fetch('/generated/external-markdown-manifest.json');
          externalManifest = externalManifestResponse.ok
            ? await externalManifestResponse.json()
            : null;
        } catch {
          externalManifest = null;
        }
        // visibility:internal (docs) + path/type fallbacks → Plattform / intern
        const items = (payload.documents ?? [])
          .filter(isPlatformNavDocument)
          .map((document) => ({
            title: document.title,
            description: document.description,
            path: resolveDocumentPortalPath(document),
            visibility: document.visibility
          }))
          .filter((item) => item.path);

        if (mounted) {
          setGeneratedPlatformItems(items);
          setGeneratedSections(buildGeneratedSections(payload.documents, docsType, externalManifest));
        }
      } catch {
        // Generated navigation is additive; keep the static sidebar if the index is unavailable.
        if (mounted) {
          setGeneratedPlatformItems([]);
          setGeneratedSections([]);
        }
      }
    }

    loadGeneratedSidebarItems();
    return () => {
      mounted = false;
    };
  }, [docsType]);

  // Open first menu by default for better UX
  const [openMenus, setOpenMenus] = React.useState(() => {
    const initial = {};
    if (docsType === 'api') {
      initial['overview'] = true;
      initial['project'] = true;
    } else {
      initial['getting-started'] = true;
    }
    return initial;
  });

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const modules = docsType === 'api' ? apiModules : frontendModules;
  const platformInternalItems = mergeSidebarItems(staticPlatformInternalItems, generatedPlatformItems)
    .filter((item) => matchesItemSearch(item, searchQuery));
  const filteredGeneratedSections = generatedSections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => !isPlatformInternalDocPath(item.path))
        .filter((item) => matchesItemSearch(item, searchQuery))
    }))
    .filter((section) => section.items.length > 0);

  const handleItemSelect = (item) => {
    if (item.route) {
      window.location.href = item.route;
      return;
    }
    onFileSelect(item.path);
  };

  const filteredModules = modules.map(module => ({
    ...module,
    items: module.items.filter(item => matchesItemSearch(item, searchQuery)),
    submenus: module.submenus ? Object.keys(module.submenus).reduce((acc, key) => {
      const filteredItems = module.submenus[key].filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredItems.length > 0) {
        acc[key] = filteredItems;
      }
      return acc;
    }, {}) : undefined
  })).filter(module =>
    module.items.length > 0 ||
    (module.submenus && Object.keys(module.submenus).length > 0) ||
    module.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-80 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        md:relative md:translate-x-0 md:z-auto md:block
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-6 h-full overflow-y-auto">
          <div className="space-y-6">
            {/* Modules */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Modules</h3>

              {filteredModules.map(module => (
                <div key={module.name} className="mb-2">
                  <button
                    onClick={() => toggleMenu(module.name)}
                    className="w-full flex items-center justify-between text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    <span>{module.title}</span>
                    <svg
                      className={`w-4 h-4 transform transition-transform ${openMenus[module.name] ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>

                  <ul className={`mt-1 ml-4 space-y-1 border-l-2 border-gray-200 pl-4 ${openMenus[module.name] ? '' : 'hidden'}`}>
                    {module.items.map((item, index) => (
                      <li key={index}>
                        <button
                          onClick={() => handleItemSelect(item)}
                          className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left"
                        >
                          {item.title}
                        </button>
                      </li>
                    ))}

                    {/* Submenus for HSE/KS */}
                    {module.submenus && Object.entries(module.submenus).map(([submenuName, submenuItems]) => (
                      <li key={submenuName}>
                        <div className="mt-2 mb-1">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{submenuName}</span>
                        </div>
                        {submenuItems.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => handleItemSelect(item)}
                            className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left ml-2"
                          >
                            {item.title}
                          </button>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Plattform / intern — plan/migration docs (visibility:internal + fallbacks) */}
            {platformInternalItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider mb-1">
                  Plattform / intern
                </h3>
                <p className="mb-3 text-xs text-amber-700/80">
                  Migreringsplaner og eierskapsregler for plattformteam — ikke produktdokumentasjon.
                </p>
                <ul className="space-y-2">
                  {platformInternalItems.map((item) => (
                    <li key={item.path}>
                      <button
                        onClick={() => onFileSelect(item.path)}
                        className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left"
                        title={item.description}
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Generated docs-owned metadata sections */}
            {filteredGeneratedSections.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Indeks</h3>
                {filteredGeneratedSections.map((section) => (
                  <div key={section.name} className="mb-3">
                    <button
                      onClick={() => toggleMenu(section.name)}
                      className="w-full flex items-center justify-between text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-3 py-2 rounded-md text-sm font-medium"
                    >
                      <span>{section.title}</span>
                      <span className="text-xs text-gray-400">{section.items.length}</span>
                    </button>
                    <ul className={`mt-1 ml-4 space-y-1 border-l-2 border-gray-200 pl-4 ${openMenus[section.name] ? '' : 'hidden'}`}>
                      {section.items.map((item) => (
                        <li key={item.path}>
                          <button
                            onClick={() => handleItemSelect(item)}
                            className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left"
                            title={item.description}
                          >
                            <span className="block">{item.title}</span>
                            <span className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-400">
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                                {item.sourceLabel ?? item.source}
                              </span>
                              <span>{item.type} · {item.status}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Core Systems - API only */}
            {docsType === 'api' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Core Systems</h3>
                <ul className="space-y-2">
                  {filterProductNavItems(apiCoreSystems).filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase())).map((item, index) => (
                    <li key={index}>
                      <button
                        onClick={() => handleItemSelect(item)}
                        className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left"
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Integrations - API only */}
            {docsType === 'api' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Integrasjoner</h3>
                <ul className="space-y-2">
                  {apiIntegrations.filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase())).map((item, index) => (
                    <li key={index}>
                      <button
                        onClick={() => handleItemSelect(item)}
                        className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-sm w-full text-left"
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* File Upload */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Custom Files</h3>
              <div className="space-y-3">
                <input
                  type="file"
                  accept=".md,.txt"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                <p className="text-xs text-gray-500">Upload custom markdown files</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocsSidebar;
